import React, { useEffect, useState, useRef } from 'react';
function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
type FAQItem = {
  question: string;
  answer: string;
};
const faqs: FAQItem[] = [{
  question: 'Do I need an appointment?',
  answer: 'Absolutely not. We welcome walk-ins all day, every day — until 8PM. Come when it works for you.'
}, {
  question: "Can you handle my kid's cut?",
  answer: "100%. We're great with kids — patient, skilled, and experienced with all styles."
}, {
  question: 'What if I have curly or long hair?',
  answer: "We specialize in all textures and lengths. Your hair type isn't a challenge for us — it's our expertise."
}, {
  question: "What if I don't like how it turns out?",
  answer: "We stand behind our work. Tell us before you leave and we'll make it right."
}, {
  question: 'Where exactly are you located?',
  answer: 'Right inside Paraíso Plaza 2 at 3300 W 84th St, Hialeah, FL 33018. Easy to find, easy to park.'
}, {
  question: 'Are you actually open late?',
  answer: 'Yes — we close at 8PM so you can come after work, after school, or whenever your day allows.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const headerRef = useScrollReveal(0);
  const listRef = useScrollReveal(200);
  const toggle = (index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  };
  return <section className="w-full py-20 sm:py-28" style={{
    backgroundColor: '#0a0a0a'
  }} aria-label="Frequently asked questions" data-id="element-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-21">
        <div ref={headerRef} className="text-center mb-14" data-id="element-22">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-23">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-24" />
            <span className="section-label" data-id="element-25">Got Questions?</span>
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-26" />
          </div>
          <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
          fontWeight: 700,
          color: '#F5F5F5',
          lineHeight: 1.2
        }} data-id="element-27">
            We've Got{' '}
            <span style={{
            color: '#C9A84C',
            fontStyle: 'italic'
          }} data-id="element-28">
              Answers
            </span>
          </h2>
        </div>

        <div ref={listRef} className="space-y-3" role="list" data-id="element-29">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => toggle(index)} index={index} data-id="element-30" />)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center" data-id="element-31">
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1rem',
          color: '#888',
          marginBottom: '1.5rem'
        }} data-id="element-32">
            Still have questions? Just call us or walk in.
          </p>
          <a href="tel:7865364035" className="btn-gold inline-flex items-center gap-3 px-8 py-4 text-base rounded-sm" data-id="element-33">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-34">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-35" />
            </svg>
            Call (786) 536-4035
          </a>
        </div>
      </div>
    </section>;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
  index
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  const answerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = answerRef.current;
    if (!el) return;
    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + 'px';
      el.style.opacity = '1';
    } else {
      el.style.maxHeight = '0';
      el.style.opacity = '0';
    }
  }, [isOpen]);
  return <div role="listitem" className="rounded-sm overflow-hidden" style={{
    backgroundColor: '#111111',
    border: `1px solid ${isOpen ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.12)'}`,
    transition: 'border-color 0.3s ease'
  }} data-id="element-36">
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} id={`faq-question-${index}`} data-id="element-37">
        <span style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '1rem',
        fontWeight: 600,
        color: isOpen ? '#C9A84C' : '#F5F5F5',
        letterSpacing: '0.03em',
        transition: 'color 0.3s ease'
      }} data-id="element-38">
          {question}
        </span>
        <div style={{
        flexShrink: 0,
        transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
        transition: 'transform 0.3s ease'
      }} data-id="element-39">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-40">
            <line x1="12" y1="5" x2="12" y2="19" data-id="element-41" />
            <line x1="5" y1="12" x2="19" y2="12" data-id="element-42" />
          </svg>
        </div>
      </button>

      <div ref={answerRef} id={`faq-answer-${index}`} role="region" aria-labelledby={`faq-question-${index}`} style={{
      maxHeight: 0,
      opacity: 0,
      overflow: 'hidden',
      transition: 'max-height 0.35s ease, opacity 0.35s ease'
    }} data-id="element-43">
        <div className="px-5 sm:px-6 pb-5 sm:pb-6" data-id="element-44">
          <div style={{
          width: '100%',
          height: '1px',
          background: 'rgba(201,168,76,0.15)',
          marginBottom: '1rem'
        }} data-id="element-45" />
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.95rem',
          color: '#aaa',
          lineHeight: 1.75
        }} data-id="element-46">
            {answer}
          </p>
        </div>
      </div>
    </div>;
}